import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { FailurePanel } from '@/shared/ui/FailurePanel';
import { Section, Sections } from '@/shared/ui/Layout';
import { PageHeader } from '@/shared/ui/PageHeader';
import { LoadingState } from '@/shared/ui/States';

import { useConnections, useConnectionSummaries } from '@/entities/connection';

import { ConnectionsTable } from './ConnectionsTable';
import { NewConnectionWizard } from './NewConnectionWizard';

import { byAttention, tally } from '../model/listing';

/* The sources this deployment can reach, as a worklist.
 *
 * **Two reads, and the second one is allowed to fail on its own.** `useConnections` gives the rows;
 * `useConnectionSummaries` gives the live harvest progress the status column renders. A failed
 * summary read costs the progress and not the screen — letting one aggregate's availability blank a
 * list of sources would make a harvest-progress query decide whether an operator can see their
 * estate, and the rows carry four honest facts without it.
 *
 * **The register wizard hides the table while it is open**, along with the empty and loading states.
 * Registering is a four-step task that asks for a credential, and the list of sources you already
 * have is not context for it — it is what you were looking at before you decided to add another.
 */
export function Connections(): ReactElement {
  const { t } = useTranslation();
  const connections = useConnections();
  const summaries = useConnectionSummaries();
  const [formOpen, setFormOpen] = useState(false);
  /* **The confirmation lives here rather than inside the form**, because registering closes the form
   * and a message inside it would unmount in the same tick. Found by registering a source in a
   * browser and getting no confirmation at all. A name, never a credential. */
  const [justRegistered, setJustRegistered] = useState<string | null>(null);

  /* Read once per render rather than per row, so every relative age on the screen is measured from
   * the same instant — two rows differencing against two clocks can report the same timestamp as
   * "59 minutes" and "1 hour". Not memoised: `new Date()` reads nothing from the query, so a
   * dependency array claiming otherwise was a lie eslint correctly refused, and a plain const
   * already gives exactly one instant per render. */
  const now = new Date();

  const byId = useMemo(() => {
    const map = new Map(
      (summaries.data?.items ?? []).map((entry) => [entry.connection_id, entry]),
    );
    return map;
  }, [summaries.data]);

  /* Sorted so the sources needing attention come first — which is what the lede promises, so it has
   * to be a mechanism rather than a sentence. Memoised because sorting on every render would reorder
   * rows mid-interaction. */
  const ordered = useMemo(
    () =>
      connections.data === undefined ? [] : [...connections.data.items].sort(byAttention),
    [connections.data],
  );

  const counts = connections.isSuccess ? tally(connections.data.items) : null;

  return (
    <Sections>
      <PageHeader
        title={t('connections.title')}
        lede={t('connections.subtitle')}
        actions={
          <Button
            tone={formOpen ? 'secondary' : 'primary'}
            aria-expanded={formOpen}
            onClick={() => {
              setFormOpen((open) => !open);
            }}
          >
            {formOpen ? t('common.cancel') : t('connections.addSource')}
          </Button>
        }
      />

      {formOpen ? (
        <NewConnectionWizard
          onFinished={(name) => {
            setJustRegistered(name);
            setFormOpen(false);
          }}
          onCancel={() => {
            setFormOpen(false);
          }}
        />
      ) : null}

      {justRegistered !== null && !formOpen ? (
        <Alert tone="notice" title={t('connections.register.registeredTitle')}>
          {t('connections.register.registeredBody', { name: justRegistered })}
        </Alert>
      ) : null}

      {/* **The sentence comes from the status, not from this screen.** It used to be one hardcoded
          hint covering every failure — "check that the platform is running" — which an approver who
          does not hold `catalog.connection:read` was shown above a detail reading `read on
          catalog.connection was denied`. `FailurePanel` derives all three from the error. */}
      {connections.isError ? (
        <FailurePanel
          error={connections.error}
          subject={t('failure.subject.connections')}
          title={t('connections.readFailed')}
          detail={connections.error.message}
          onRetry={() => {
            void connections.refetch();
          }}
        />
      ) : null}

      {connections.isPending && !formOpen ? (
        <LoadingState rows={3} label={t('connections.loading')} />
      ) : null}

      {connections.isSuccess && ordered.length === 0 && !formOpen ? (
        <EmptyState
          title={t('connections.emptyTitle')}
          guidance={t('connections.emptyGuidance')}
          action={
            <Button
              tone="primary"
              onClick={() => {
                setFormOpen(true);
              }}
            >
              {t('connections.addSource')}
            </Button>
          }
        />
      ) : null}

      {connections.isSuccess && ordered.length > 0 && !formOpen ? (
        <Section
          title={t('connections.registered')}
          hint={
            counts === null
              ? undefined
              : /* Composed from four ICU messages rather than one sentence with four plurals,
                   because the empty branches must contribute nothing — "3 verified, 0 failed" reads
                   as a problem where "3 verified" reads as the answer. */
                `${t('connections.tally', { verified: counts.verified })}${t(
                  'connections.tallyUnverified',
                  { count: counts.unverified },
                )}${t('connections.tallyFailed', { count: counts.failed })}${t(
                  'connections.tallyDisabled',
                  { count: counts.disabled },
                )}`
          }
        >
          <ConnectionsTable connections={ordered} summaries={byId} now={now} />

          {/* **Reported rather than silent**, because a failed summary read costs the status column
              its live harvest progress and nothing on screen would otherwise say so: a source
              mid-harvest would read as merely `verified`, which is a true state and the wrong answer
              to "what is happening to this source". Not an `Alert` — the rows underneath are still
              valid, and an error banner over valid data teaches a reader to distrust it. */}
          {summaries.isError ? (
            <p className="lq-source__note">{t('connections.progressUnread')}</p>
          ) : null}

          {/* Registering does not contact the source, and a reader who does not know that concludes
              a `registered` row is broken. Stated once under the table rather than per row. */}
          <p className="lq-source__note">{t('connections.registerIsNotAContact')}</p>
        </Section>
      ) : null}
    </Sections>
  );
}
